import React from 'react';
import { Helmet } from 'react-helmet-async';
import { Container, Typography, Box, Grid, Card, Button, Avatar } from '@mui/material';
import { Nature, Phone } from '@mui/icons-material';
import Layout from '../components/Layout';

const glassStyle = {
  background: 'rgba(255, 255, 255, 0.25)',
  backdropFilter: 'blur(10px)',
  border: '1px solid rgba(255, 255, 255, 0.18)',
  borderRadius: '16px',
  boxShadow: '0 8px 32px 0 rgba(31, 38, 135, 0.37)'
};

const LandscapeMaintenance = () => {
  return (
    <Layout>
      <Helmet>
        <title>Landscape Maintenance Vancouver BC | Seasonal Cleanup West Vancouver | Garden Care</title>
        <meta name="description" content="Professional landscape maintenance Vancouver BC. Seasonal cleanup, pruning, mulching in West Vancouver & North Vancouver. Year-round garden care. Free quotes (604) 202-0053" />
        <meta name="keywords" content="landscape maintenance vancouver, seasonal cleanup west vancouver, garden maintenance north vancouver, pruning services bc, mulching vancouver, landscape care vancouver" />
        <link rel="canonical" href="https://greenmountainlandscaping.ca/landscape-maintenance" />
      </Helmet>
      
      <Box
        sx={{
          minHeight: '60vh',
          display: 'flex',
          alignItems: 'center',
          position: 'relative',
          overflow: 'hidden',
          '&::before': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundImage: 'url("/images/10.webp")',
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            zIndex: -2
          },
          '&::after': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'linear-gradient(135deg, rgba(27, 94, 32, 0.8) 0%, rgba(46, 125, 50, 0.7) 100%)',
            zIndex: -1
          }
        }}
      >
        <Container maxWidth="lg">
          <Box sx={{ ...glassStyle, p: { xs: 3, md: 6 }, color: 'white', textAlign: 'center' }}>
            <Avatar sx={{ bgcolor: 'white', color: 'success.main', width: 80, height: 80, mx: 'auto', mb: 3 }}>
              <Nature sx={{ fontSize: 40 }} />
            </Avatar>
            <Typography variant="h1" component="h1" sx={{ mb: 3, textShadow: '2px 2px 4px rgba(0,0,0,0.5)' }}>
              Professional Landscape Maintenance Vancouver BC
            </Typography>
            <Typography variant="h5" sx={{ mb: 4, opacity: 0.9 }}>
              Keep your outdoor space pristine year-round
            </Typography>
            <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', flexWrap: 'wrap' }}>
              <Button variant="contained" size="large" href="tel:+16042020053" startIcon={<Phone />} sx={{ bgcolor: 'white', color: 'primary.main' }}>
                (604) 202-0053
              </Button>
              <Button variant="outlined" size="large" href="/#contact" sx={{ borderColor: 'white', color: 'white' }}>
                Get Maintenance Quote
              </Button>
            </Box>
          </Box>
        </Container>
      </Box>
      
      <Container maxWidth="lg" sx={{ py: 10 }}>
        <Typography variant="h1" component="h1" sx={{ mb: 4, color: 'primary.main' }}>
          Professional Landscape Maintenance Vancouver BC
        </Typography>
        
        <Typography variant="body1" sx={{ mb: 4, fontSize: '1.1rem' }}>
          Keep your outdoor space pristine year-round with expert <strong>landscape maintenance in Vancouver</strong>. Green Mountain Landscaping provides comprehensive seasonal cleanup, pruning, and garden care throughout West Vancouver, North Vancouver, and Metro Vancouver.
        </Typography>

        <Typography variant="h2" component="h2" sx={{ mb: 3, color: 'primary.main' }}>
          Year-Round Garden Care Vancouver
        </Typography>

        <Grid container spacing={4} sx={{ mb: 6 }}>
          {[
            { title: 'Seasonal Cleanup West Vancouver', desc: 'Spring and fall cleanup services' },
            { title: 'Pruning & Trimming Services', desc: 'Professional tree and shrub maintenance' },
            { title: 'Mulching & Soil Care', desc: 'Soil health and moisture retention' },
            { title: 'Weed Control Vancouver', desc: 'Safe and effective weed management' },
            { title: 'Plant Health Monitoring', desc: 'Disease prevention and plant care' },
            { title: 'Winter Garden Preparation', desc: 'Protect your landscape through winter' }
          ].map((service, index) => (
            <Grid item xs={12} sm={6} md={4} key={index}>
              <Card sx={{ 
                ...glassStyle, 
                p: 3, 
                height: '100%',
                transition: 'transform 0.3s ease',
                '&:hover': { transform: 'translateY(-4px)' }
              }}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <Avatar sx={{ bgcolor: 'success.main', mr: 2, width: 48, height: 48 }}>
                    <Nature />
                  </Avatar>
                  <Typography variant="h6" sx={{ fontWeight: 600 }}>{service.title}</Typography>
                </Box>
                <Typography variant="body2" color="text.secondary">{service.desc}</Typography>
              </Card>
            </Grid>
          ))}
        </Grid>

        <Typography variant="h3" component="h3" sx={{ mb: 3, color: 'primary.main' }}>
          How Much Does Landscape Maintenance Cost in Vancouver?
        </Typography>
        
        <Typography variant="body1" sx={{ mb: 4 }}>
          Our <strong>seasonal cleanup West Vancouver</strong> and maintenance services are competitively priced and tailored to your property's specific needs. We offer flexible maintenance packages to keep your landscape healthy and beautiful throughout all seasons.
        </Typography>

        <Box sx={{ textAlign: 'center', mt: 6, p: 4, bgcolor: 'success.main', borderRadius: 4, color: 'white' }}>
          <Typography variant="h4" sx={{ mb: 2 }}>Ready for Year-Round Care?</Typography>
          <Typography variant="body1" sx={{ mb: 3, opacity: 0.9 }}>Keep your landscape beautiful in every season</Typography>
          <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', flexWrap: 'wrap' }}>
            <Button variant="contained" size="large" href="tel:+16042020053" sx={{ bgcolor: 'white', color: 'success.main' }}>
              Call (604) 202-0053
            </Button>
            <Button variant="outlined" size="large" href="/#contact" sx={{ borderColor: 'white', color: 'white' }}>
              Get Maintenance Quote
            </Button>
          </Box>
        </Box>
      </Container>
    </Layout>
  );
};

export default LandscapeMaintenance;